import { Component, OnInit, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehicleUnitMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { UnitsService } from "@app/main/modules/foundation/_services/unit.service";
import { DeviceComparisonStatus, DeviceParameterComparisonResultMap, DeviceParameterProfileMaterialisationOptions, DeviceProfileService, IDeviceParameterProfile, IDeviceParameterRecord } from "@app/services/device-profile.service";
import { TranslationService } from "@app/services/translation.service";
import { VehicleService } from "@app/services/vehicle.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { lastValueFrom } from "rxjs";
import { tap } from "rxjs/operators";
import { DeviceProfileComparisonStatusLabelComponent } from "./device-profile-comparison-status-label/device-profile-comparison-status-label.component";
import { DeviceProfileEditorComponent } from "./device-profile-editor/device-profile-editor.component";
import './device-profiles-manager.component.scss';

@UntilDestroy()
@Component({
    selector: 'device-profiles-manager',
    template: require('./device-profiles-manager.component.html')
})
export class DeviceProfilesManagerComponent implements OnInit {
    @ViewChild('profilesTable') profilesTable: VTableComponent;
    @ViewChild('vehiclesTable') vehiclesTable: VTableComponent;
    @ViewChild('deviceProfileEditor', { static: false }) deviceProfileEditor: DeviceProfileEditorComponent;

    vehicles: IVehicle[];
    selectedVehicle: IVehicle;

    deviceParameterComparisonResultMap: DeviceParameterComparisonResultMap;
    deviceParameterProfiles: IDeviceParameterProfile[];
    selectedDeviceProfile: IDeviceParameterProfile;
    latestDeviceParameterRecords: IDeviceParameterRecord[];
    unitMappings: IVehicleUnitMapping[];

    loading: boolean;
    deleting: boolean;

    vehicleFilterTabs: IFilterBarTab<IVehicle>[] = [];
    vehicleFilterEvent: IAggregateFilterEvent<IVehicle>;

    //Tables
    profilesTableColumns: IVTableColumn<IDeviceParameterProfile>[];
    profilesTableRows: IVTableRow[];

    vehiclesTableColumns: IVTableColumn<IVehicle>[];
    vehiclesTableRows: IVTableRow[];

    constructor(
        private readonly translationService: TranslationService,
        private readonly deviceProfileService: DeviceProfileService,
        private readonly vehicleService: VehicleService,
        private readonly vTableService: VTableService,
        private readonly unitService: UnitsService
    ) {
    }

    ngOnInit() {
        this.createTables();
        this.getData();
        this.subscribeToChanges();
    }

    onConfirmDeleteButton(confirmed: boolean): void {
        if (!confirmed) {
            return;
        }

        this.delete(this.selectedDeviceProfile);
    }

    onCloseDeviceProfileForm(): void {

        this.clearSelectedDeviceProfile();
    }

    onDeviceProfileSaved(deviceProfile: IDeviceParameterProfile): void {
        this.onCloseDeviceProfileForm();
    }

    onProfileTableRowClicked(tableRow: IVTableRow): void {
        if (!tableRow.data) {
            return;
        }

        this.selectDeviceProfile(tableRow.data);
    }

    onVehicleTableRowClicked(tableRow: IVTableRow): void {
        if (!tableRow.data) {
            return;
        }

        this.selectVehicle(tableRow.data);
    }

    createNewDeviceProfile(): void {
        this.selectDeviceProfile(this.deviceProfileService.createDeviceParameterProfileTemplate());
    }

    selectDeviceProfile(profile: IDeviceParameterProfile): void {
        this.selectedDeviceProfile = profile;
    }

    clearSelectedDeviceProfile(): void {
        this.selectedDeviceProfile = null;
    }

    selectVehicle(vehicle: IVehicle): void {
        this.selectedVehicle = vehicle;
    }

    clearSelectedVehicle(): void {
        this.selectedVehicle = null;
    }

    switchToEditProfile(): void {
        if (!this.selectedVehicle?.deviceProfile) {
            return;
        }

        this.selectDeviceProfile(this.selectedVehicle.deviceProfile);
        this.clearSelectedVehicle();
    }

    selectedTabChanged(): void {
        this.resetTable();
    }

    //--

    private async delete(deviceProfile: IDeviceParameterProfile): Promise<void> {
        if (!deviceProfile?.Id) {
            return;
        }

        this.deleting = true;

        await this.deviceProfileService.deleteProfile(deviceProfile);

        this.clearSelectedDeviceProfile();

        this.deleting = false;
    }

    private async getData(invalidateCache: boolean = false): Promise<void> {

        if (this.loading) {
            return;
        }

        this.loading = true;

        this.deviceProfileService.getProfiles({ Active: true, MaterialisationOptions: DeviceParameterProfileMaterialisationOptions.WithMappings }, invalidateCache).pipe(untilDestroyed(this)).subscribe({
            next: deviceParameterProfiles => {
                this.deviceParameterProfiles = deviceParameterProfiles.filter(pr => pr.Active);
                this.mapAndRenderData();
            }
        })

        const promises: Promise<any>[] = [
            (this.vehicleService.getVehicles({ includePeripheralMappings: true, includeLatestPlots: true }) as Promise<IVehicle[]>),
            this.deviceProfileService.getComparisonsForAllVehicles(),
            this.deviceProfileService.getLatestDeviceParameterRecords(),
            lastValueFrom(this.unitService.getAllUnitMappings())
        ];

        await Promise.all(promises).then(([vehicles, deviceParameterComparisonResultMap, latestDeviceParameterRecords, unitMappings]) => {
            this.vehicles = vehicles;
            this.deviceParameterComparisonResultMap = deviceParameterComparisonResultMap;
            this.latestDeviceParameterRecords = latestDeviceParameterRecords;
            this.unitMappings = unitMappings;
            this.mapAndRenderData();
        });

        this.loading = false;
    }

    private mapAndRenderData(): void {
        if (!this.deviceParameterProfiles || !this.deviceParameterComparisonResultMap) {
            return;
        }

        this.deviceProfileService.mapDeviceProfilesToVehicles(this.deviceParameterProfiles, this.vehicles);
        this.deviceProfileService.mapDeviceParameterComparisonResultsToVehicles(this.deviceParameterComparisonResultMap, this.vehicles);
        this.deviceProfileService.mapDeviceEventTimeToVehicles(this.latestDeviceParameterRecords, this.unitMappings, this.vehicles);

        this.addDecoratingDataToProfiles(this.deviceParameterProfiles);

        this.createTabs();
        this.createTableRows();
    }

    private addDecoratingDataToProfiles(deviceProfiles: IDeviceParameterProfile[]): void {
        if (!deviceProfiles?.length) {
            return;
        }

        deviceProfiles.forEach(profile => {
            profile.parametersSetLabel = this.deviceProfileService.getParameterLabelForProfile(profile)

            this.deviceProfileService.createMappingPillsForDeviceProfile(profile);
        });
    }

    private subscribeToChanges(): void {
        this.deviceProfileService.deviceProfilesUpdated.pipe(
            untilDestroyed(this),
            tap(profiles => {
                this.deviceParameterProfiles = profiles.filter(profile => profile.Active);
                this.mapAndRenderData();
            }))
            .subscribe()
    }

    private createTabs(): void {
        this.vehicleFilterTabs = [];

        this.createTabDefinition("Common.All", (vehicle) => true);
        this.createTabDefinition("DeviceProfilesManager.SynchronizedTabTitle", (vehicle) => DeviceComparisonStatus.Synchronized === this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult));
        this.createTabDefinition("DeviceProfilesManager.AwaitingSyncTabTitle", (vehicle) => DeviceComparisonStatus.AwaitingSync === this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult));
        this.createTabDefinition("DeviceProfilesManager.IncompatibleUnitTabTitle", (vehicle) => DeviceComparisonStatus.IncompatibleUnit === this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult));
        this.createTabDefinition("DeviceProfilesManager.NoUnitInstalledTabTitle", (vehicle) => DeviceComparisonStatus.NoUnitInstalled === this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult));
        this.createTabDefinition("DeviceProfilesManager.NoProfileAssigned", (vehicle) => DeviceComparisonStatus.NoProfileAssigned === this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult));
    }

    private createTabDefinition(translationString: string, callback: (vehicle: IVehicle) => boolean): void {
        this.vehicleFilterTabs.push({ title: this.translationService.t(translationString), predicate: callback });
    }

    private createTables(): void {
        this.createProfilesTable();
        this.createVehiclesTable();
    }

    private createProfilesTable(): void {
        this.profilesTableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceProfilesManager.TableColumnTitleName", width: 200 }),
                createCellFunction: profile => this.vTableService.createTableCell(profile.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceProfilesManager.TableColumnTitleAppliesTo", width: 200 }),
                createCellFunction: profile => this.vTableService.createTableCell(profile.appliesToLabel)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceProfilesManager.TableColumnTitleParametersSet", sortType: SortType.Number }),
                createCellFunction: profile => this.vTableService.createTableCell(profile.parametersSetLabel)
            },
            {

                header: this.vTableService.createDateTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleLastUpdated' }),
                createCellFunction: profile => this.vTableService.createDateCell(profile.UpdatedTime)
            },
            {

                header: this.vTableService.createDateTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleCreated' }),
                createCellFunction: profile => this.vTableService.createDateCell(profile.CreatedTime)
            }
        ]
    }

    private createVehiclesTable(): void {
        this.vehiclesTableColumns = [
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: vehicle => this.vTableService.createVehicleRegCell(vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleProfileAssigned', width: 260 }),
                createCellFunction: vehicle => this.vTableService.createTableCell(vehicle.deviceProfile?.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleStatus', width: 420, sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: vehicle => this.vTableService.createTableCell(vehicle.deviceParameterComparisonResult, {
                    component: DeviceProfileComparisonStatusLabelComponent,
                    componentInputs: {
                        comparisonResult: vehicle.deviceParameterComparisonResult
                    },
                    sortData: this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle.deviceParameterComparisonResult),
                    exportData: this.deviceProfileService.getDeviceParameterComparisonResultStatusLabel(vehicle.deviceParameterComparisonResult),
                    searchData: this.deviceProfileService.getDeviceParameterComparisonResultStatusLabel(vehicle.deviceParameterComparisonResult),
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleParameters' }),
                createCellFunction: vehicle => this.vTableService.createTableCell(
                    this.deviceProfileService.getDescriptiveDeviceParameterComparisonResultLabel(vehicle.deviceParameterComparisonResult)
                )
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleLastConnected' }),
                createCellFunction: vehicle => this.vTableService.createDateCell(vehicle.Plot?.Utc)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'DeviceProfilesManager.TableColumnTitleLastSynced' }),
                createCellFunction: vehicle => this.vTableService.createDateCell(vehicle.deviceLastSynced)
            }
        ]
    }

    private createTableRows(): void {
        this.profilesTableRows = this.vTableService.createTableRows(this.deviceParameterProfiles, this.profilesTableColumns);
        this.vehiclesTableRows = this.vTableService.createTableRows(this.vehicles, this.vehiclesTableColumns);
    }

    private resetTable(): void {
        setTimeout(() => {
            // resolves issue with tables and cdk scroller
            this.profilesTable?.render();
            this.vehiclesTable?.render();
        })
    }
}
